import type { ThunkDispatch, UnknownAction } from '@reduxjs/toolkit'
import { z } from 'zod'
import { baseApi } from '@/shared/api'
import { productApi, type ProductStock } from '@/entities/product'
import type { WsClient, WsEnvelope } from '@/shared/ws'

/**
 * Реестр подписок приложения: топики бэкенда → правка кеша RTK Query. Живёт в слое app —
 * только он выше entities и вправе звать их api. Событие никогда не рендерится напрямую: оно
 * либо точечно правит кеш (`updateQueryData`), либо инвалидирует тег (рефетч), либо ляжет в
 * слайс (когда появится экран). Доставка в несуществующий кеш не заводится — вместо неё
 * топик просто числится в реестре с комментарием.
 */

/** Окно батча живых остатков: прайс на тысячу позиций иначе устроит шторм ререндеров. */
const STOCK_BATCH_MS = 300

type ApiState = { [baseApi.reducerPath]: ReturnType<typeof baseApi.reducer> }
type Dispatch = ThunkDispatch<ApiState, unknown, UnknownAction>
type GetState = () => ApiState

/** Типы плунжера для провайдера: дублировать форму стора в двух местах незачем. */
export type WsApiState = ApiState
export type WsDispatch = Dispatch

export type WsContext = {
  /** Активное юрлицо сеанса (`user.legalEntity.unp`); `null` — юрлица нет, топиков по нему тоже. */
  unp: string | null
  /** Текущий логин (`user.id`): команды 1С и уведомления адресуются по нему. */
  userId: string
}

/** Имена топиков строятся в одном месте — чтобы фронт и бэкенд читали один реестр. */
export const WS_TOPICS = {
  /** Живые остатки всего каталога: событие по любому товару правит кеш, где этот товар есть. */
  stockAll: 'stock:all',
  /** Остатки одного товара — альтернатива для карточки; доставку ведёт тот же батч по id. */
  stockOne: (id: string) => `stock:${id}`,
  /** Резервы и счета юрлица: статусы строк и новые счета — рефетч списков. */
  orders: (unp: string) => `orders:${unp}`,
  /** Новые документы юрлица. Домена документов на фронте ещё нет — топик только зарегистрирован. */
  documents: (unp: string) => `documents:${unp}`,
  /** Ход команд обмена с 1С текущего логина: резерв, счёт, правка, персональный прайс. */
  commands: (userId: string) => `commands:${userId}`,
  /** Лента уведомлений логина: `notification.new {id,kind}` — сигнал перечитать ленту и счётчик. */
  notifications: (userId: string) => `notifications:${userId}`,
} as const

/** `stock.changed`: новые остатки товара. `all:true` — «перечитай список» при >1000 номеров. */
const stockChangedSchema = z.object({
  id: z.string().optional(),
  all: z.boolean().optional(),
  availableToOrder: z.string().nullable().optional(),
  availabilityLevel: z.enum(['over-20', '11-20', '6-10']).nullable().optional(),
  orderable: z.boolean().optional(),
  incomingSupply: z.boolean().optional(),
  incomingNote: z.string().nullable().optional(),
})

type StockPatch = z.infer<typeof stockChangedSchema>

/** `command.updated`: команда 1С сменила статус — инвалидируем её кеш и очередь монитора. */
const commandUpdatedSchema = z.object({
  id: z.string(),
  status: z.string().optional(),
})

/** `notification.new`: появилось уведомление — сигнал перечитать ленту (истина в таблице, не в событии). */
const notificationNewSchema = z.object({
  id: z.string(),
  kind: z.string().optional(),
})

/** Правит только пришедшие поля остатка — остальное в кеше не трогаем. */
function applyStock(target: ProductStock, patch: StockPatch): void {
  if (patch.availableToOrder !== undefined) target.availableToOrder = patch.availableToOrder
  if (patch.availabilityLevel !== undefined) target.availabilityLevel = patch.availabilityLevel
  if (patch.orderable !== undefined) target.orderable = patch.orderable
  if (patch.incomingSupply !== undefined) target.incomingSupply = patch.incomingSupply
  if (patch.incomingNote !== undefined) target.incomingNote = patch.incomingNote
}

/**
 * Батч остатков: события копятся окном 300 мс и объединяются по id товара (позднее значение
 * старше). На флаш — точечная правка тех запросов каталога/карточки, что уже в кеше, одним
 * проходом. `all:true` — список целиком перечитывается инвалидацией тега, точечно не правим.
 */
function createStockBatcher(dispatch: Dispatch, getState: GetState) {
  let timer: ReturnType<typeof setTimeout> | null = null
  const pending = new Map<string, StockPatch>()
  let rereadAll = false

  function flush(): void {
    timer = null

    if (rereadAll) {
      rereadAll = false
      pending.clear()
      dispatch(baseApi.util.invalidateTags([{ type: 'Product', id: 'LIST' }]))
      return
    }

    const byId = new Map(pending)
    pending.clear()
    const state = getState()

    for (const args of productApi.util.selectCachedArgsForQuery(state, 'getProducts')) {
      dispatch(
        productApi.util.updateQueryData('getProducts', args, (draft) => {
          for (const row of draft.items) {
            const patch = byId.get(row.id)
            if (patch) applyStock(row.stock, patch)
          }
        }),
      )
    }

    for (const args of productApi.util.selectCachedArgsForQuery(state, 'getProduct')) {
      const patch = byId.get(args.id)
      if (!patch) continue
      dispatch(
        productApi.util.updateQueryData('getProduct', args, (draft) => applyStock(draft.stock, patch)),
      )
    }
  }

  return {
    handle(envelope: WsEnvelope): void {
      const parsed = stockChangedSchema.safeParse(envelope.payload)
      if (!parsed.success) {
        console.warn('[ws] stock.changed без ожидаемых полей', parsed.error.issues)
        return
      }

      const data = parsed.data
      if (data.all) {
        rereadAll = true
      } else if (data.id !== undefined) {
        pending.set(data.id, { ...pending.get(data.id), ...data })
      } else {
        return
      }

      if (timer === null) timer = setTimeout(flush, STOCK_BATCH_MS)
    },
    cancel(): void {
      if (timer) clearTimeout(timer)
      timer = null
      pending.clear()
      rereadAll = false
    },
  }
}

/**
 * Подписывает приложение на все топики контекста и возвращает снятие подписок. Вызывается при
 * живом сеансе, пересобирается при смене юрлица. Активные подписки после реконнекта клиент
 * восстанавливает сам.
 */
export function registerWsSubscriptions(
  client: WsClient,
  dispatch: Dispatch,
  getState: GetState,
  ctx: WsContext,
): () => void {
  const offs: Array<() => void> = []
  const stock = createStockBatcher(dispatch, getState)

  // Живые остатки каталога/карточки/прайса.
  offs.push(client.subscribe(WS_TOPICS.stockAll, stock.handle))

  // Команды обмена с 1С: статус пришёл сокетом — снимаем ручной опрос монитора/корзины.
  offs.push(
    client.subscribe(WS_TOPICS.commands(ctx.userId), (envelope) => {
      const parsed = commandUpdatedSchema.safeParse(envelope.payload)
      if (!parsed.success) {
        console.warn('[ws] command.updated без id', parsed.error.issues)
        return
      }
      dispatch(
        baseApi.util.invalidateTags([
          { type: 'Command', id: parsed.data.id },
          { type: 'Command', id: 'LIST' },
        ]),
      )
    }),
  )

  if (ctx.unp !== null) {
    // Резервы, счета, причины invoice.new/reserve.status/orders.all-* — рефетч списков.
    offs.push(
      client.subscribe(WS_TOPICS.orders(ctx.unp), () => {
        dispatch(
          baseApi.util.invalidateTags([
            { type: 'Order', id: 'LINES' },
            { type: 'Order', id: 'INVOICES' },
          ]),
        )
      }),
    )

    // documents:<unp> (document.created) — домена документов на фронте пока нет (нет api/тега).
    // Топик зарегистрирован здесь и подключится, когда появится экран документов; подписку с
    // пустым обработчиком не заводим — это был бы мёртвый код (process-coverage §0, §5).
  }

  // Лента уведомлений логина: событие — сигнал «перечитай». Детали добирает запрос, поэтому
  // точечно кеш не правим, а инвалидируем список — рефетч ленты и счётчика непрочитанных (§11).
  offs.push(
    client.subscribe(WS_TOPICS.notifications(ctx.userId), (envelope) => {
      const parsed = notificationNewSchema.safeParse(envelope.payload)
      if (!parsed.success) {
        console.warn('[ws] notification.new без id', parsed.error.issues)
        return
      }
      dispatch(baseApi.util.invalidateTags([{ type: 'Notification', id: 'LIST' }]))
    }),
  )

  return () => {
    stock.cancel()
    offs.forEach((off) => off())
  }
}
